export type FileType = 'image' | 'video' | 'audio' | 'file';

interface FileTypeStrategy {
  getFileType(fileName: string): FileType;
  isImage(fileName: string): boolean;
  isVideo(fileName: string): boolean;
  isAudio(fileName: string): boolean;
  getSupportedExtensions(fileType: FileType): string[];
}

class FileTypeDetector implements FileTypeStrategy {
  private imageExtensions: Set<string>;
  private videoExtensions: Set<string>;
  private audioExtensions: Set<string>;

  constructor() {
    this.imageExtensions = new Set([
      'jpg',
      'jpeg',
      'png',
      'gif',
      'bmp',
      'webp',
      'svg',
      'tiff',
      'tif',
      'ico',
      'heic',
      'heif',
      'jfif',
      'pjpeg',
      'pjp',
    ]);

    this.videoExtensions = new Set([
      'mp4',
      'avi',
      'mov',
      'wmv',
      'flv',
      'mkv',
      'webm',
      'mpeg',
      'mpg',
      '3gp',
      'm4v',
      'ogv',
      'qt',
      'divx',
      'rm',
      'rmvb',
      'asf',
      'vob',
    ]);

    this.audioExtensions = new Set([
      'mp3',
      'wav',
      'ogg',
      'flac',
      'aac',
      'm4a',
      'wma',
      'aiff',
      'opus',
      'mid',
      'midi',
      'amr',
      'wv',
      'ape',
      'au',
      'ra',
      'ram',
    ]);
  }

  /**
   *
   * @param fileName
   * @returns
   */
  public getFileType(fileName: string): FileType {
    const extension = this.extractExtension(fileName)?.toLowerCase();

    if (!extension) {
      return 'file';
    }

    if (this.imageExtensions.has(extension)) {
      return 'image';
    }

    if (this.videoExtensions.has(extension)) {
      return 'video';
    }

    if (this.audioExtensions.has(extension)) {
      return 'audio';
    }

    return 'file';
  }

  /**
   * @param fileName
   * @returns
   */

  private extractExtension(fileName: string): string | undefined {
    const cleanName = fileName?.split('?')[0];

    const lastDotIndex = cleanName?.lastIndexOf('.');

    if (lastDotIndex === -1) {
      return '';
    }

    return cleanName?.slice(lastDotIndex + 1);
  }

  public isImage(fileName: string): boolean {
    return this.getFileType(fileName) === 'image';
  }

  public isVideo(fileName: string): boolean {
    return this.getFileType(fileName) === 'video';
  }

  public isAudio(fileName: string): boolean {
    return this.getFileType(fileName) === 'audio';
  }

  public getSupportedExtensions(fileType: FileType): string[] {
    switch (fileType) {
      case 'image':
        return Array.from(this.imageExtensions);
      case 'video':
        return Array.from(this.videoExtensions);
      case 'audio':
        return Array.from(this.audioExtensions);
      default:
        return [];
    }
  }

  public addExtension(extension: string, fileType: FileType): void {
    const ext = extension.toLowerCase().replace('.', '');

    switch (fileType) {
      case 'image':
        this.imageExtensions.add(ext);
        break;
      case 'video':
        this.videoExtensions.add(ext);
        break;
      case 'audio':
        this.audioExtensions.add(ext);
        break;
      default:
        //
        break;
    }
  }

  public removeExtension(extension: string, fileType: FileType): void {
    const ext = extension.toLowerCase().replace('.', '');

    switch (fileType) {
      case 'image':
        this.imageExtensions.delete(ext);
        break;
      case 'video':
        this.videoExtensions.delete(ext);
        break;
      case 'audio':
        this.audioExtensions.delete(ext);
        break;
      default:
        break;
    }
  }

  public hasExtension(extension: string, fileType: FileType): boolean {
    const ext = extension.toLowerCase().replace('.', '');

    switch (fileType) {
      case 'image':
        return this.imageExtensions.has(ext);
      case 'video':
        return this.videoExtensions.has(ext);
      case 'audio':
        return this.audioExtensions.has(ext);
      default:
        return false;
    }
  }

  public getAllExtensions(): Record<FileType, string[]> {
    return {
      image: Array.from(this.imageExtensions),
      video: Array.from(this.videoExtensions),
      audio: Array.from(this.audioExtensions),
      file: [],
    };
  }
}

class FileTypeDetectorSingleton {
  private static instance: FileTypeDetector;

  private constructor() {}

  public static getInstance(): FileTypeDetector {
    if (!FileTypeDetectorSingleton.instance) {
      FileTypeDetectorSingleton.instance = new FileTypeDetector();
    }
    return FileTypeDetectorSingleton.instance;
  }
}

class File {
  private name: string;
  private type: FileType;
  private detector: FileTypeDetector;

  constructor(fileName: string, detector?: FileTypeDetector) {
    this.name = fileName;
    this.detector = detector || new FileTypeDetector();
    this.type = this.detector.getFileType(fileName);
  }

  public getName(): string {
    return this.name;
  }

  public getType(): FileType {
    return this.type;
  }

  public getExtension(): string {
    const lastDotIndex = this.name.lastIndexOf('.');
    return lastDotIndex !== -1
      ? this.name.slice(lastDotIndex + 1).toLowerCase()
      : '';
  }

  public isImage(): boolean {
    return this.type === 'image';
  }

  public isVideo(): boolean {
    return this.type === 'video';
  }

  public isAudio(): boolean {
    return this.type === 'audio';
  }

  public isFile(): boolean {
    return this.type === 'file';
  }

  public toString(): string {
    return `File: ${this.name}, Type: ${this.type}, Extension: ${this.getExtension()}`;
  }
}

class FileManager {
  private files: File[] = [];
  private detector: FileTypeDetector;

  constructor(detector?: FileTypeDetector) {
    this.detector = detector || new FileTypeDetector();
  }

  public addFile(fileName: string): File {
    const file = new File(fileName, this.detector);
    this.files.push(file);
    return file;
  }

  public addFiles(fileNames: string[]): File[] {
    const newFiles = fileNames.map((name) => new File(name, this.detector));
    this.files.push(...newFiles);
    return newFiles;
  }

  public getFilesByType(fileType: FileType): File[] {
    return this.files.filter((file) => file.getType() === fileType);
  }

  public getImageFiles(): File[] {
    return this.getFilesByType('image');
  }

  public getVideoFiles(): File[] {
    return this.getFilesByType('video');
  }

  public getAudioFiles(): File[] {
    return this.getFilesByType('audio');
  }

  public getOtherFiles(): File[] {
    return this.getFilesByType('file');
  }

  public getAllFiles(): File[] {
    return [...this.files];
  }

  public clear(): void {
    this.files = [];
  }

  public count(): number {
    return this.files.length;
  }

  public countByType(fileType: FileType): number {
    return this.getFilesByType(fileType).length;
  }
}

export { File, FileManager, FileTypeDetector, FileTypeDetectorSingleton };
export type { FileTypeStrategy };

export const adjustBucketName = new FileTypeDetector();
